import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    // Without IntersectionObserver keep the preview image; never eagerly fetch the video.
    if (!('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData) return;
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#loadVideo();
        else this.refs.videoElement.pause();
      }
    });
    this.#observer.observe(this);
  }

  #loadVideo() {
    const { videoSources, videoElement } = this.refs;
    let changed = false;
    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource && !source.hasAttribute('src')) {
        source.setAttribute('src', videoSource);
        changed = true;
      }
    }
    if (changed) videoElement.load();
    videoElement.play().catch(() => {});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.refs.videoElement?.pause();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
